import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useProducts } from '@/hooks/useProducts';
import { formatMoney } from '@/lib/format';
import ProductImage from '@/components/feature/ProductImage';
import Modal from '@/components/base/Modal';
import type { Product } from '@/lib/types';

export default function ProductsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { products, loading, error, hasMore, loadMore, reload } = useProducts({ search });
  const [viewing, setViewing] = useState<Product | null>(null);

  const toggleActive = async (product: Product) => {
    const { error: e } = await supabase
      .from('products')
      .update({ active: !product.active, updated_at: new Date().toISOString() })
      .eq('id', product.id);
    if (e) return;
    reload();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Productos</h1>
          <p className="mt-1 text-foreground-600">Gestiona tu catálogo, precios y existencias.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/products/import"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-file-excel-2-line" /> Importar Excel
          </Link>
          <Link
            to="/products/new"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-add-line" /> Nuevo producto
          </Link>
        </div>
      </div>

      <div className="mt-6 relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, SKU, código de barra o marca..."
          className="w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-4 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
        />
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-box-3-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">No hay productos todavía.</p>
          <Link
            to="/products/new"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-add-line" /> Crear el primer producto
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-6 rounded-lg border border-background-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-background-100 text-foreground-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Producto</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium">Código de barra</th>
                  <th className="px-4 py-3 font-medium text-right">Precio</th>
                  <th className="px-4 py-3 font-medium text-right">Stock</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-background-100 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <ProductImage
                          url={product.image_url}
                          name={product.name}
                          className="w-10 h-10 rounded-md object-cover bg-background-100 shrink-0"
                          placeholderClassName="w-10 h-10 rounded-md shrink-0 p-0"
                          iconClassName="text-base"
                          showName={false}
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground-950 truncate">{product.name}</p>
                          <p className="text-xs text-foreground-500 truncate">
                            {product.category?.name || 'Sin categoría'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground-700">{product.sku || '—'}</td>
                    <td className="px-4 py-3 text-foreground-700">{product.barcode || '—'}</td>
                    <td className="px-4 py-3 text-right font-medium text-foreground-950">
                      {formatMoney(Number(product.sale_price))}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`font-medium ${
                          product.stock <= 0
                            ? 'text-accent-700'
                            : product.stock <= (product.min_stock || 0)
                              ? 'text-accent-600'
                              : 'text-foreground-900'
                        }`}
                      >
                        {product.stock}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          product.active
                            ? 'bg-accent-100 text-accent-900'
                            : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        <i className={`ri-circle-fill text-[6px] ${product.active ? 'text-accent-500' : 'text-foreground-400'}`} />
                        {product.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setViewing(product)}
                          title="Ver"
                          className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-600 hover:bg-background-200 cursor-pointer"
                        >
                          <i className="ri-eye-line" />
                        </button>
                        <button
                          onClick={() => navigate(`/products/${product.id}`)}
                          title="Editar"
                          className="w-8 h-8 rounded-md flex items-center justify-center text-primary-700 hover:bg-primary-50 cursor-pointer"
                        >
                          <i className="ri-pencil-line" />
                        </button>
                        <button
                          onClick={() => toggleActive(product)}
                          title={product.active ? 'Desactivar' : 'Activar'}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-600 hover:bg-background-200 cursor-pointer"
                        >
                          <i className={product.active ? 'ri-toggle-line' : 'ri-toggle-fill'} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="mt-5 flex justify-center">
              <button
                onClick={loadMore}
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
              >
                <i className="ri-arrow-down-line" /> Cargar más
              </button>
            </div>
          )}
        </>
      )}

      {viewing && (
        <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing.name}>
          <div className="space-y-3">
            <div className="w-full h-40 rounded-md overflow-hidden bg-background-100">
              <ProductImage
                url={viewing.image_url}
                name={viewing.name}
                className="w-full h-full object-cover"
                placeholderClassName="w-full h-full"
                iconClassName="text-4xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-foreground-500">Precio de venta</p>
                <p className="font-medium text-foreground-950">{formatMoney(Number(viewing.sale_price))}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Stock</p>
                <p className="font-medium text-foreground-950">{viewing.stock}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">SKU</p>
                <p className="font-medium text-foreground-950">{viewing.sku || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Código de barra</p>
                <p className="font-medium text-foreground-950">{viewing.barcode || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Categoría</p>
                <p className="font-medium text-foreground-950">{viewing.category?.name || 'Sin categoría'}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500">Marca</p>
                <p className="font-medium text-foreground-950">{viewing.brand || '—'}</p>
              </div>
            </div>
            {viewing.description && (
              <div>
                <p className="text-xs text-foreground-500">Descripción</p>
                <p className="text-sm text-foreground-700">{viewing.description}</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}